import {
  type DropdownOption,
  type Option,
  type FileType,
  FileTypeValues
} from '@dfg/brahma-types';
import { DEFAULT_PROFILE_INITIALS } from '../constants/strings';

export const parseString = (str: string) => {
  try {
    return JSON.parse(str);
  } catch (e) {
    return str;
  }
};

export const replaceString = (
  str: string,
  key: string,
  value: string
): string => {
  return str.replaceAll(key, value);
};

// generating url for query params
export const getUrlForQueryParams = (url: string, values: object = {}) => {
  const arr = Object.entries(values);
  if (arr.length === 0) {
    return url;
  }
  const queryParams: string = arr.reduce(
    (prev: string, [key, value]: [string, string], currIndex: number) => {
      if (currIndex === arr.length - 1) return prev + key + '=' + value;
      return prev + key + '=' + value + '&';
    },
    ''
  );
  return `${url}?${queryParams}`;
};

export const capitalizeFirstLetter = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

export const minimizeFirstLetter = (string: string) => {
  return string.charAt(0).toLowerCase() + string.slice(1);
};

export const isEmptyString = (str: string = '') => {
  return str?.trim() === '';
};

export const isEmptyDropDownOption = (option: DropdownOption) => {
  const value = (option as Option)?.value;
  return !value;
};

export const checkStringLength = (str: string, length: number) => {
  return str.length < length;
};
export const truncateString = (
  str: string,
  maxLength: number,
  omission: string = '...'
): string => {
  if (str.length <= maxLength) {
    return str;
  }

  const truncated = str.slice(0, maxLength);
  return truncated + omission;
};

export const isString = (param: any) => {
  return typeof param === 'string';
};

// used for string interpolation ${key} replaced with value in {key: value}
export const interpolateString = (
  template: string,
  values: Record<string, string | boolean>
): string => {
  return template?.replace(/#{(.*?)}/g, (_, key) => {
    const replacement = values[key.trim()];
    return replacement !== undefined ? String(replacement) : '';
  });
};

export const toTitleCase = (str: string) => {
  return str.replace(/\b\w/g, function (char) {
    return char.toUpperCase();
  });
};

export const getFileType = (data: string = ''): FileType => {
  if (data.includes(FileTypeValues.image)) {
    return FileTypeValues.image;
  }
  if (data.includes(FileTypeValues.pdf)) {
    return FileTypeValues.pdf;
  }
  if (data.includes(FileTypeValues.audio)) {
    return FileTypeValues.audio;
  }
  return FileTypeValues.image;
};

export const recursiveTrim = (value: any): any => {
  try {
    // Handle null and undefined
    if (value == null) {
      return value;
    }

    // Handle strings
    if (typeof value === 'string') {
      return value.trim();
    }

    // Handle arrays
    if (Array.isArray(value)) {
      return value.map((item: any) => recursiveTrim(item));
    }

    // Handle objects
    if (typeof value === 'object') {
      return Object.keys(value).reduce((acc: any, key) => {
        acc[key] = recursiveTrim(value[key]);
        return acc;
      }, {});
    }

    // Return other types as is (numbers, booleans, etc.)
    return value;
  } catch (e) {
    return value;
  }
};

// abcEfgHi -> Abc Efg Hi
export const formatCamelToTitleCase = (str: string) => {
  return str
    .split(/(?=[A-Z])/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// abc_efg_hi -> Abc Efg Hi
export const formatSnakeToTitleCase = (str: string) => {
  return str
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const safeDecodeURIComponent = <T extends string | undefined | null>(
  value: T
): T => {
  if (typeof value !== 'string') return value;

  try {
    return decodeURIComponent(value) as T;
  } catch {
    return value;
  }
};

export const encodeValue = (value: string) => {
  try {
    return btoa(value);
  } catch {
    return value;
  }
};

export const roundToDecimals = (num: number, decimals: number = 2): number => {
  // Handle edge cases (NaN, Infinity, etc.)
  if (isNaN(num)) return NaN;
  if (!isFinite(num)) return num;
  if (!Number.isInteger(decimals) || decimals < 0) {
    throw new Error('Decimals must be a non-negative integer');
  }

  // Calculate the precision multiplier (e.g., 100 for 2 decimals)
  const multiplier = 10 ** decimals;

  // Add Number.EPSILON to mitigate floating-point rounding errors.
  // Example for 1.005 with 2 decimals:
  //   (1.005 + EPSILON) * 100 ≈ 100.50000000000001 → rounds to 101 → 1.01
  return Math.round((num + Number.EPSILON) * multiplier) / multiplier;
};

export const formatDocumentTypeNameToImgUrl = (name: string) => {
  if (!name) return '';
  return name.replace(/\//g, ':');
};

/**
 * Generates user initials from first and last name.
 *
 * @param user - User object containing firstName and lastName (both optional)
 * @returns A string containing the uppercase initials (e.g., "JD" for "John Doe"),
 *          or the default profile initials if names are not available
 *
 * @example
 * ```typescript
 * const user = { firstName: 'John', lastName: 'Doe' };
 * const initials = getUserInitials(user); // Returns "JD"
 *
 * const userWithoutName = {};
 * const initials = getUserInitials(userWithoutName); // Returns "PC" (default)
 * ```
 */
export const getUserInitials = (user: {
  firstName?: string;
  lastName?: string;
}): string => {
  const { firstName = '', lastName = '' } = user;
  const firstInitial = firstName.charAt(0).toUpperCase();
  const lastInitial = lastName.charAt(0).toUpperCase();
  const initials = firstInitial + lastInitial;
  return initials || DEFAULT_PROFILE_INITIALS;
};

/**
 * Joins an array of strings with a separator
 * @param strings - Array of strings to join
 * @param separator - Separator to use (default: ', ')
 * @returns Joined string
 */
export const joinStrings = (
  strings: string[] = [],
  separator: string = ', '
): string => {
  return strings.filter(Boolean).join(separator);
};

/**
 * Formats a comment string by ensuring it starts with a bullet character (•).
 * If the comment already starts with •, it is returned as-is.
 * Otherwise, • is prepended.
 */
export const formatCommentWithBullet = (comment: string): string => {
  const trimmed = comment.trim();
  if (trimmed.startsWith('•')) return trimmed;
  return `• ${trimmed}`;
};
